import {
    Outlet, useParams, useMatch, useNavigate,
} from 'react-router-dom';
import { useMemo } from 'react';
import { useOpenModal } from '@rainbow-modules/hooks';
import { RenderIf } from 'react-rainbow-components';
import ButtonIcon from 'components/ButtonIcon';
import CustomerProfile from 'components/CustomerProfile';
import BrainIcon from 'components/icons/brain';
import ListIcon from 'components/icons/list';
import ArrowLeftIcon from 'components/icons/arrowLeft';
import FilesIcon from 'components/icons/files';
import Phone from 'components/icons/phone';
import OpportunityIcon from 'components/icons/opportunity';
import DocumentIcon from 'components/icons/document';
import Nexhealth from 'components/icons/nexhealth';
import { HasRoles } from '@voxityai/auth/components';
import { useHasRequiredRoles } from '@voxityai/auth/hooks';
import { auth } from 'data/firestore/firebase';
import { StyledBreadcrumb, StyledBreadcrumbs } from 'components/Breadcrumbs';
import useCustomer from 'data/firestore/agent/customer/use';
import useManageCustomers from 'data/hooks/useManageCustomers';
import useNavigateWithQuery from 'hooks/useNavigateWithQuery';
import useAgentData from 'hooks/useAgentData';
import { Provider } from './context';
import {
    Columns,
    Container,
    LeftColumn,
    RightColumn,
    StyledCard,
    StyledTabset,
    StyledTab,
    TabLabel,
    StyledButton,
    HeaderContainer,
    FooterContainer,
} from './styled';
import { ADD_EDIT_CUSTOMER_MODAL } from '../../../constants';

const CustomerDetails = () => {
    const { agentId = '', customerId = '' } = useParams();
    const { hasAccess: isAdmin } = useHasRequiredRoles({ auth, allowedRoles: ['admin'] });
    const { isAppEnabled } = useAgentData();
    const navigate = useNavigate();
    const navigateWithQuery = useNavigateWithQuery();
    const match = useMatch(':agentId/customers/:customerId/:tabName');

    const { data: profileData, isLoading } = useCustomer(
        agentId,
        customerId,
        {
            disabled: !agentId || !customerId,
        },
    );
    const [editCustomer] = useOpenModal(ADD_EDIT_CUSTOMER_MODAL);

    const { remove } = useManageCustomers({ agentId });

    const contextValue = useMemo(() => ({
        customer: profileData,
        isLoading,
    }), [profileData, isLoading]);

    return (
        <Container>
            <HeaderContainer>
                <ButtonIcon
                    icon={<ArrowLeftIcon />}
                    onClick={() => navigateWithQuery(`/${agentId}/customers`)}
                    size="small"
                />
                <StyledBreadcrumbs>
                    <StyledBreadcrumb
                        label="Customers"
                        onClick={() => navigate(`/${agentId}/customers`)}
                    />
                    <StyledBreadcrumb label="Customer Details" />
                </StyledBreadcrumbs>
            </HeaderContainer>
            <Columns>
                <LeftColumn>
                    <StyledCard>
                        <CustomerProfile
                            agentId={agentId}
                            customerId={customerId}
                            showAdditionalInfo
                            showMetadata
                            footer={(
                                <FooterContainer>
                                    <StyledButton
                                        label="Edit Customer"
                                        variant="brand"
                                        shaded
                                        borderRadius="semi-rounded"
                                        onClick={() => editCustomer({
                                            mode: 'edit',
                                            header: 'Edit customer',
                                            customerData: profileData,
                                        })}
                                    />
                                    <HasRoles auth={auth} allowedRoles={['admin']}>
                                        <StyledButton
                                            label="Delete Customer"
                                            variant="destructive"
                                            borderRadius="semi-rounded"
                                            onClick={() => remove(customerId)}
                                        />
                                    </HasRoles>
                                </FooterContainer>
                            )}
                        />
                    </StyledCard>
                </LeftColumn>
                <RightColumn>
                    <StyledTabset
                        variant="line"
                        activeTabName={match?.params?.tabName}
                        onSelect={(event, eventName) => {
                            navigate(eventName);
                            event.preventDefault();
                        }}
                    >
                        <StyledTab
                            name="activity"
                            label={(
                                <TabLabel>
                                    <ListIcon />
                                    Activity
                                </TabLabel>
                            )}
                        />
                        <StyledTab
                            name="phone-numbers"
                            label={(
                                <TabLabel>
                                    <Phone />
                                    Numbers
                                </TabLabel>
                            )}
                        />
                        <StyledTab
                            name="opportunities"
                            label={(
                                <TabLabel>
                                    <OpportunityIcon />
                                    Opportunities
                                </TabLabel>
                            )}
                        />
                        <RenderIf isTrue={isAppEnabled('invoice-builder')}>
                            <StyledTab
                                name="invoices"
                                label={(
                                    <TabLabel>
                                        <DocumentIcon />
                                        Invoices
                                    </TabLabel>
                                )}
                            />
                        </RenderIf>
                        <RenderIf isTrue={isAppEnabled('nexhealth')}>
                            <StyledTab
                                name="nexhealth"
                                label={(
                                    <TabLabel>
                                        <Nexhealth />
                                        NexHealth
                                    </TabLabel>
                                )}
                            />
                        </RenderIf>
                        <StyledTab
                            name="files"
                            label={(
                                <TabLabel>
                                    <FilesIcon />
                                    Files
                                </TabLabel>
                            )}
                        />
                        {isAdmin && (
                            <StyledTab
                                name="memory"
                                label={(
                                    <TabLabel>
                                        <BrainIcon />
                                        Memory
                                    </TabLabel>
                                )}
                            />
                        )}
                    </StyledTabset>
                    <Provider value={contextValue}>
                        <Outlet />
                    </Provider>
                </RightColumn>
            </Columns>
        </Container>
    );
};

export default CustomerDetails;
